// src/pages/BrandDetail/components/BrandActivitySection.js
import { useLanguage } from '../../../contexts/LanguageContext';
import Chart from 'react-apexcharts';
import './BrandActivitySection.css';

const BrandActivitySection = ({ brandDetails }) => {
  const { translate } = useLanguage();

  if (!brandDetails?.distributions || !brandDetails?.search_trend_data) return null;

  const { type, category } = brandDetails.distributions;
  const { time_series } = brandDetails.search_trend_data;

  // Transform search trend data to heatmap format (year x month)
  const transformToHeatmapData = () => {
    const labels = time_series?.trend_data?.labels || [];
    const values = time_series?.trend_data?.log_values || [];

    // Group by year
    const yearMonthData = {};
    labels.forEach((label, index) => {
      const [year, month] = label.split('-');
      if (!yearMonthData[year]) {
        yearMonthData[year] = {};
      }
      yearMonthData[year][parseInt(month)] = values[index];
    });

    // Convert to ApexCharts series format
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const years = Object.keys(yearMonthData).sort().reverse();

    return years.map(year => ({
      name: year,
      data: monthNames.map((monthName, monthIndex) => ({
        x: monthName,
        y: yearMonthData[year]?.[monthIndex + 1] || null
      }))
    }));
  };

  // Prepare funnel chart data for product types
  const prepareFunnelData = () => {
    if (!type?.labels || !type?.values) return [];

    const combined = type.labels.map((label, i) => ({
      x: label,
      y: type.values[i] || 0
    })).filter(item => item.y > 0);

    return combined.sort((a, b) => b.y - a.y).slice(0, 5);
  };

  // Prepare radial bar data for categories
  const prepareRadialData = () => {
    if (!category?.labels || !category?.values) return { series: [], labels: [] };

    const total = category.values.reduce((sum, val) => sum + val, 0);
    const combined = category.labels.map((label, i) => ({
      label,
      value: category.values[i] || 0,
      percentage: total > 0 ? Math.round((category.values[i] / total) * 100) : 0
    })).filter(item => item.value > 0);

    const sorted = combined.sort((a, b) => b.value - a.value).slice(0, 4);

    return {
      series: sorted.map(item => item.percentage),
      labels: sorted.map(item => item.label),
      total
    };
  };

  const heatmapSeries = transformToHeatmapData();
  const funnelData = prepareFunnelData();
  const radialData = prepareRadialData();

  // Get trend info
  const getTrendText = (direction) => {
    if (direction === '상승' || direction === 'increasing') return 'Rising';
    if (direction === '하락' || direction === 'decreasing') return 'Falling';
    return 'Stable';
  };

  // Find peak month
  const findPeakMonth = () => {
    const labels = time_series?.trend_data?.labels || [];
    const values = time_series?.trend_data?.log_values || [];
    if (labels.length === 0) return '-';

    let maxIndex = 0;
    let maxValue = values[0];
    values.forEach((val, i) => {
      if (val > maxValue) {
        maxValue = val;
        maxIndex = i;
      }
    });

    const [year, month] = labels[maxIndex].split('-');
    return `${year.slice(-2)}-${month}`;
  };

  // Heatmap chart options
  const heatmapOptions = {
    chart: {
      type: 'heatmap',
      height: 250,
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false }
    },
    colors: ['#6366F1'],
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 4,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: [
            { from: 13, to: 14.5, color: '#E0E7FF', name: 'Low' },
            { from: 14.51, to: 15.5, color: '#818CF8', name: 'Medium' },
            { from: 15.51, to: 20, color: '#4338CA', name: 'High' }
          ]
        }
      }
    },
    dataLabels: { enabled: false },
    xaxis: {
      labels: { style: { fontSize: '9px', colors: '#94a3b8' } }
    },
    yaxis: {
      labels: { style: { fontSize: '10px', fontWeight: 600, colors: '#475569' } }
    },
    grid: { padding: { right: 10 } },
    stroke: { width: 1, colors: ['#fff'] },
    tooltip: {
      y: {
        formatter: (val) => val ? val.toFixed(2) : '-'
      }
    }
  };

  // Funnel chart options
  const funnelOptions = {
    chart: {
      type: 'bar',
      height: 250,
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        horizontal: true,
        barHeight: '80%',
        isFunnel: true
      }
    },
    colors: ['#0F172A', '#334155', '#475569', '#64748B', '#94A3B8'],
    dataLabels: {
      enabled: true,
      formatter: function (val, opt) {
        return opt.w.globals.labels[opt.dataPointIndex];
      },
      dropShadow: { enabled: false },
      style: { colors: ['#fff'], fontSize: '10px' }
    },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { show: false } },
    legend: { show: false },
    tooltip: {
      y: { formatter: (val) => val + ' items' }
    }
  };

  // Radial bar chart options
  const radialOptions = {
    chart: {
      height: 280,
      type: 'radialBar',
      fontFamily: 'Inter, sans-serif'
    },
    colors: ['#6366F1', '#8B5CF6', '#EC4899', '#10B981'],
    plotOptions: {
      radialBar: {
        dataLabels: {
          name: { fontSize: '14px' },
          value: { fontSize: '14px' },
          total: {
            show: true,
            label: 'Overview',
            formatter: () => radialData.total || 0
          }
        },
        hollow: { size: '30%' },
        track: { background: '#F1F5F9' }
      }
    },
    labels: radialData.labels,
    legend: {
      show: true,
      position: 'bottom',
      fontSize: '11px',
      itemMargin: { horizontal: 3, vertical: 0 }
    }
  };

  const totalProducts = type?.values?.reduce((sum, val) => sum + val, 0) || 0;
  const totalCategories = category?.values?.reduce((sum, val) => sum + val, 0) || 0;

  return (
    <section className="glass-card brand-activity-section">
      {/* Header */}
      <div className="brand-activity-header">
        <div>
          <h2 className="font-serif brand-activity-title">
            {translate('brandDetailPage.distribution.brandActivity') || 'Brand Activity'}
          </h2>
          <div className="brand-activity-subtitle-row">
            <p className="brand-activity-subtitle">Product composition and market interest analysis</p>
            <span className="health-badge-small">
              {brandDetails.brand_metrics?.brand_health_category || 'Unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* 3-Column Grid */}
      <div className="brand-activity-grid">
        {/* 1. Search Volume Card */}
        <div className="activity-card search-volume-card">
          <div className="activity-card-header">
            <div>
              <h3 className="activity-card-title">Search Volume</h3>
              <p className="activity-card-subtitle">Interest over time</p>
            </div>
            <div className="activity-card-value">
              {(time_series?.average / 1000000)?.toFixed(1) || '0'}M
            </div>
          </div>

          <div className="heatmap-chart-container">
            <Chart
              options={heatmapOptions}
              series={heatmapSeries}
              type="heatmap"
              height={220}
            />
          </div>

          <div className="search-stats-row">
            <div className="search-stat">
              <span className="search-stat-label">Trend</span>
              <div className="search-stat-value">
                <span className="stat-dot stat-dot-gray"></span>
                {getTrendText(time_series?.trend?.direction)}
              </div>
            </div>
            <div className="search-stat search-stat-border">
              <span className="search-stat-label">Peak</span>
              <div className="search-stat-value">{findPeakMonth()}</div>
            </div>
            <div className="search-stat search-stat-border">
              <span className="search-stat-label">Change</span>
              <div className={`search-stat-value ${time_series?.trend?.percentage < 0 ? 'text-rose' : 'text-emerald'}`}>
                {time_series?.trend?.percentage < 0 ? '↓' : '↑'} {Math.abs(time_series?.trend?.percentage || 0).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* 2. Product Types Card */}
        <div className="activity-card">
          <div className="activity-card-header-simple">
            <h3 className="activity-card-title">Product Types</h3>
            <span className="activity-card-count">{totalProducts} Items</span>
          </div>
          <div className="funnel-chart-container">
            <Chart
              options={funnelOptions}
              series={[{ name: 'Products', data: funnelData }]}
              type="bar"
              height={250}
            />
          </div>
        </div>

        {/* 3. Categories Card */}
        <div className="activity-card">
          <div className="activity-card-header-simple">
            <h3 className="activity-card-title">Categories</h3>
            <span className="activity-card-count">{totalCategories} SKUs</span>
          </div>
          <div className="radial-chart-container">
            <Chart
              options={radialOptions}
              series={radialData.series}
              type="radialBar"
              height={280}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandActivitySection;
